import { ImportField, ImportRow } from './ImportGridTypes';
import { createRecordAdapter } from './recordAdapter';

// True when every data cell in the row is at its "untouched" default — empty
// string / undefined / null, empty array (custom cells), or `false` (boolean
// default). A freshly added blank row matches; the moment the user types
// anything (even flipping a boolean to true) the row is considered touched.
export const isRowFullyEmpty = (row: ImportRow, fields: ImportField[]): boolean =>
	fields.every((field) => {
		const value = row[field.key];
		if (value === undefined || value === null) return true;
		if (typeof value === 'string' && value.trim() === '') return true;
		if (Array.isArray(value) && value.length === 0) return true;
		if (field.type === 'boolean' && value === false) return true;
		return false;
	});

// Run the optional consumer recordHook and required + unique constraint checks
// against every row in `rows`. Returns rows with refreshed `_errors` and any
// value mutations the recordHook may have made via adapter.set().
//
// Single source of truth for validation across the upload (handleMappingComplete,
// handleFieldValueMappingComplete, handleManualEntry) and the in-grid edit path.
//
// Two passes: recordHook first across all rows so its mutations are visible,
// then constraint checks against post-hook values. This is required for `unique`,
// which compares each row against every other row — comparing pre-hook values
// would miss duplicates introduced (or resolved) by the hook.
//
// Fully-empty rows (blank rows waiting for input) are skipped — they neither
// produce required/unique errors nor participate in uniqueness comparisons, so
// an untouched blank row doesn't surface as a "row with errors".
export const validateRows = (
	rows: ImportRow[],
	fields: ImportField[],
	recordHook?: (record: any) => any
): ImportRow[] => {
	const staged = rows.map((row) => {
		const adapter = createRecordAdapter(
			Object.fromEntries(
				Object.entries(row).filter(([key]) => key !== '_rowId' && key !== '_errors')
			)
		);

		const fullyEmpty = isRowFullyEmpty(row, fields);
		if (recordHook && !fullyEmpty) {
			recordHook(adapter);
		}

		return { row, adapter, fullyEmpty };
	});

	const postHookValuesByRowId: Record<string, Record<string, any>> = {};
	staged.forEach(({ row, adapter }) => {
		postHookValuesByRowId[row._rowId] = adapter.getValues();
	});

	return staged.map(({ row, adapter, fullyEmpty }) => {
		if (fullyEmpty) {
			return {
				...row,
				_rowId: row._rowId,
				_errors: {},
			};
		}

		fields.forEach((field) => {
			(field.constraints || []).forEach((constraint) => {
				if (constraint.type === 'required') {
					const val = adapter.get(field.key);
					if (val === undefined || val === null || val === '') {
						adapter.addError(field.key, `${field.label} is required`);
					}
				}
				if (constraint.type === 'unique') {
					const val = adapter.get(field.key);
					if (val !== undefined && val !== null && val !== '') {
						const hasDuplicate = staged.some(
							({ row: other, fullyEmpty: otherEmpty }) =>
								!otherEmpty &&
								other._rowId !== row._rowId &&
								postHookValuesByRowId[other._rowId][field.key] === val
						);
						if (hasDuplicate) {
							adapter.addError(field.key, `${field.label} must be unique`);
						}
					}
				}
			});
		});

		return {
			...row,
			...adapter.getValues(),
			_rowId: row._rowId,
			_errors: adapter.getErrors(),
		};
	});
};
